import type { DefaultValues } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { ImportFormat } from '@/modules/api/codegen';
import { Form, handleFormSubmit, useTranslateSubmitError } from '@/modules/forms';
import { TagManager } from '@/services/utilities';

import { Fields } from '../../constants';
import { importFormSchema, type ImportFormSchemaType, type ImportFormValues } from '../../schemas';
import { startImport } from '../../services/actions';
import { Delimiter } from '../../types';
import { ImportFormFields } from './ImportFormFields';

const defaultValues = {
    format: ImportFormat.passwd,
    delimiter: Delimiter.COMMA,
    replaceSecrets: false,
} satisfies DefaultValues<ImportFormValues>;

export interface ImportFormProps {
    vaultId?: string;
    onSuccess?: () => void;
}

export const ImportForm = ({ vaultId, onSuccess }: ImportFormProps) => {
    const dispatch = useDispatch();

    const translate = useTranslateSubmitError<ImportFormValues>();

    return (
        <Form<ImportFormSchemaType, ImportFormValues>
            schema={importFormSchema}
            defaultValues={{ ...defaultValues, vaultId }}
            onSubmit={async (values, { setError, resetField, reset }) => {
                // A cancelled or failed import resolves without a field error too, so success is tracked explicitly.
                let imported = false;

                const error = await handleFormSubmit<ImportFormValues>((values, actions) => {
                    TagManager.push('used_import_export');
                    dispatch(
                        startImport(values, {
                            ...actions,
                            success: () => {
                                imported = true;
                                actions.success();
                            },
                        }),
                    );
                })(values);

                if (error) {
                    const [errorField] = error;

                    const translatedError = translate(...error);
                    setError(...translatedError);

                    if (errorField === Fields.SECRETS) resetField(errorField, { keepError: true });
                } else if (imported) {
                    reset();
                    onSuccess?.();
                }
            }}
        >
            <ImportFormFields withReplaceSecrets={!vaultId} />
        </Form>
    );
};
